import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { motion } from 'framer-motion';
import { PHONE_DISPLAY, PHONE_E164, whatsappUrl } from '../store/contactInfo';
import { submitRequirementForm } from '../services/api';

const ORANGE = '#FF6B00';
const DARK = '#1A1A1A';
const MUTED = '#555';

const industries = [
  'Manufacturing',
  'Logistics / 3PL',
  'Automobile',
  'Pharmaceutical',
  'Food & Beverage',
  'FMCG',
  'Retail',
  'E-commerce',
  'Chemical',
];

const applications = [
  'Factory / Production Area',
  'Warehouse',
  'Distribution Centre',
  'Cold Storage',
  'Spare Parts Store',
  'Finished Goods Storage',
  'Raw Material Storage',
];

const storageTypes = [
  'Pallets',
  'Cartons',
  'Plastic Bins',
  'Metal Components',
  'Pipes / Bars',
  'Coils',
  'Dies / Moulds',
];

const handlingEquipment = [
  'Forklift',
  'Reach Truck',
  'Stacker',
  'Hand Pallet Truck',
  'Manual',
  'Crane',
];

const priorities = [
  'Maximum Storage Capacity',
  'Easy Accessibility',
  'Fast Loading & Unloading',
  'High Throughput',
  'Low Investment Cost',
  'Future Expansion',
  'Unsure (Recommend the best solution)',
];

const objectives = [
  'Increase storage capacity',
  'Utilize available height',
  'Store more SKUs',
  'Faster picking',
  'FIFO inventory',
  'LIFO inventory',
  'Improve safety',
  'Replace floor stacking',
  'Organize production materials',
  'Unsure (Please recommend)',
];

const accessories = [
  'Decking',
  'Rack Guards',
  'Column Protectors',
  'Mesh Back Panels',
  'Pallet Supports',
  'Safety Signage',
  'Not Sure',
];

const attachmentOptions = [
  'Layout Drawing',
  'Product Photos',
  'Pallet Photos',
  'Existing Storage Photos',
  'Product Dimensions',
];

const initialState = {
  companyName: '',
  contactPerson: '',
  designation: '',
  mobile: '',
  email: '',
  projectLocation: '',
  industry: [],
  industryOther: '',
  application: [],
  applicationOther: '',
  projectType: '',
  completionDate: '',
  storageTypes: [],
  storageOther: '',
  weightKg: '',
  dimensions: '',
  quantity: '',
  spaceLength: '',
  spaceWidth: '',
  spaceHeight: '',
  layoutAvailable: '',
  handling: [],
  handlingOther: '',
  maxLiftHeight: '',
  priorities: [],
  objectives: [],
  accessories: [],
  attachments: [],
  specialRequirements: '',
};

function FormSection({ number, title, subtitle, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e6e6e6',
        borderLeft: `4px solid ${ORANGE}`,
        borderRadius: 0,
        p: { xs: 2.25, md: 3 },
        mb: 2.5,
        bgcolor: '#fff',
      }}
    >
      <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: '0.78rem', letterSpacing: '0.14em', mb: 0.5 }}>
        SECTION {number}
      </Typography>
      <Typography sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.15rem', md: '1.3rem' }, mb: subtitle ? 0.5 : 1.5 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: MUTED, mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>{subtitle}</Typography>
      ) : null}
      {children}
    </Paper>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={onChange} sx={{ color: '#bbb', '&.Mui-checked': { color: ORANGE } }} />}
      label={label}
      sx={{
        alignItems: 'flex-start',
        m: 0,
        '& .MuiFormControlLabel-label': { color: '#444', fontSize: '0.95rem', lineHeight: 1.45, pt: 0.35 },
      }}
    />
  );
}

function toggleInList(list, item) {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

function formatList(items, other) {
  const base = items.length ? items.join(', ') : '—';
  return other ? `${base}${items.length ? '; ' : ''}Other: ${other}` : base;
}

function buildSubmissionMessage(data) {
  return [
    'PALLET RACKING REQUIREMENT',
    '',
    '1. Customer Details',
    `Company: ${data.companyName || '—'}`,
    `Contact: ${data.contactPerson || '—'}`,
    `Designation: ${data.designation || '—'}`,
    `Mobile: ${data.mobile || '—'}`,
    `Email: ${data.email || '—'}`,
    `Location: ${data.projectLocation || '—'}`,
    `Industry: ${formatList(data.industry, data.industryOther)}`,
    '',
    '2. Project Information',
    `Application: ${formatList(data.application, data.applicationOther)}`,
    `Project Type: ${data.projectType || '—'}`,
    `Preferred Completion: ${data.completionDate || '—'}`,
    '',
    '3. Storage Requirements',
    `Store: ${formatList(data.storageTypes, data.storageOther)}`,
    `Weight: ${data.weightKg ? `${data.weightKg} kg` : '—'}`,
    `Dimensions: ${data.dimensions || '—'}`,
    `Quantity: ${data.quantity || '—'}`,
    '',
    '4. Available Space',
    `L×W×H: ${data.spaceLength || '?'}m × ${data.spaceWidth || '?'}m × ${data.spaceHeight || '?'}m`,
    `Layout Available: ${data.layoutAvailable || '—'}`,
    '',
    '5. Material Handling',
    `Equipment: ${formatList(data.handling, data.handlingOther)}`,
    `Max Lift Height: ${data.maxLiftHeight || '—'}`,
    '',
    '6. Storage Priorities',
    formatList(data.priorities),
    '',
    '7. Main Objective',
    formatList(data.objectives),
    '',
    '8. Accessories',
    formatList(data.accessories),
    '',
    '9. Additional',
    `Can Attach: ${formatList(data.attachments)}`,
    `Special Requirements: ${data.specialRequirements || '—'}`,
  ].join('\n');
}

export default function PalletRackingForm() {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitRequirementForm(form);
      setSubmitted(true);
      const message = buildSubmissionMessage(form);
      window.open(whatsappUrl(message), '_blank', 'noopener,noreferrer');
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit requirement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      id="requirement-form"
      sx={{
        py: { xs: 7, md: 10 },
        bgcolor: '#f7f7f7',
        borderTop: '1px solid rgba(0,0,0,0.06)',
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Typography variant="overline" sx={{ color: ORANGE, fontWeight: 900, letterSpacing: 3, display: 'block', mb: 1 }}>
            Requirement form
          </Typography>
          <Typography
            sx={{
              fontWeight: 900,
              color: DARK,
              fontSize: { xs: '1.75rem', md: '2.35rem' },
              lineHeight: 1.15,
              mb: 1,
            }}
          >
            Pallet Racking Requirement Form
          </Typography>
          <Typography sx={{ color: MUTED, lineHeight: 1.7, mb: 3.5, fontSize: { xs: '1rem', md: '1.05rem' }, maxWidth: 680 }}>
            Most fields are simple to fill — no rack terminology needed. Share what you know today; our sales engineer
            will confirm technical details during a site visit or follow-up call.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <FormSection number="01" title="Customer Details">
              <Grid container spacing={2}>
                {[
                  { key: 'companyName', label: 'Company Name', required: true },
                  { key: 'contactPerson', label: 'Contact Person', required: true },
                  { key: 'designation', label: 'Designation' },
                  { key: 'mobile', label: 'Mobile Number', required: true },
                  { key: 'email', label: 'Email', type: 'email' },
                  { key: 'projectLocation', label: 'Project Location' },
                ].map((field) => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <TextField
                      fullWidth
                      required={field.required}
                      type={field.type || 'text'}
                      label={field.label}
                      value={form[field.key]}
                      onChange={(e) => update(field.key, e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography sx={{ fontWeight: 700, color: DARK, mt: 2.5, mb: 1 }}>Industry</Typography>
              <FormGroup>
                <Grid container>
                  {industries.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.industry.includes(item)}
                        onChange={() => update('industry', toggleInList(form.industry, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              <TextField
                fullWidth
                label="Other industry"
                value={form.industryOther}
                onChange={(e) => update('industryOther', e.target.value)}
                sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
            </FormSection>

            <FormSection number="02" title="Project Information">
              <Typography sx={{ fontWeight: 700, color: DARK, mb: 1 }}>Application</Typography>
              <FormGroup>
                <Grid container>
                  {applications.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.application.includes(item)}
                        onChange={() => update('application', toggleInList(form.application, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              <TextField
                fullWidth
                label="Other application"
                value={form.applicationOther}
                onChange={(e) => update('applicationOther', e.target.value)}
                sx={{ mt: 1, mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />

              <Typography sx={{ fontWeight: 700, color: DARK, mb: 1 }}>Is this</Typography>
              <RadioGroup value={form.projectType} onChange={(e) => update('projectType', e.target.value)}>
                {['New Installation', 'Expansion of Existing Storage', 'Replacement of Existing Racks'].map((item) => (
                  <FormControlLabel
                    key={item}
                    value={item}
                    control={<Radio sx={{ color: '#bbb', '&.Mui-checked': { color: ORANGE } }} />}
                    label={item}
                    sx={{ '& .MuiFormControlLabel-label': { color: '#444', fontSize: '0.95rem' } }}
                  />
                ))}
              </RadioGroup>

              <TextField
                fullWidth
                type="date"
                label="Preferred Completion Date"
                value={form.completionDate}
                onChange={(e) => update('completionDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
            </FormSection>

            <FormSection number="03" title="Storage Requirements" subtitle="Tell us what you need to store — approximate values are fine.">
              <Typography sx={{ fontWeight: 700, color: DARK, mb: 1 }}>What would you like to store?</Typography>
              <FormGroup>
                <Grid container>
                  {storageTypes.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.storageTypes.includes(item)}
                        onChange={() => update('storageTypes', toggleInList(form.storageTypes, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              <TextField
                fullWidth
                label="Other storage type"
                value={form.storageOther}
                onChange={(e) => update('storageOther', e.target.value)}
                sx={{ mt: 1, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Approx. weight per pallet/item (kg)"
                    value={form.weightKg}
                    onChange={(e) => update('weightKg', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Approx. dimensions (L × W × H)"
                    value={form.dimensions}
                    onChange={(e) => update('dimensions', e.target.value)}
                    placeholder="e.g. 1200 × 1000 × 1500 mm"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Approximate quantity to store"
                    value={form.quantity}
                    onChange={(e) => update('quantity', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  />
                </Grid>
              </Grid>
            </FormSection>

            <FormSection number="04" title="Available Space">
              <Grid container spacing={2}>
                {[
                  { key: 'spaceLength', label: 'Length (m)' },
                  { key: 'spaceWidth', label: 'Width (m)' },
                  { key: 'spaceHeight', label: 'Clear Height (m)' },
                ].map((field) => (
                  <Grid item xs={12} sm={4} key={field.key}>
                    <TextField
                      fullWidth
                      label={field.label}
                      value={form[field.key]}
                      onChange={(e) => update(field.key, e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Typography sx={{ fontWeight: 700, color: DARK, mt: 2.5, mb: 1 }}>Warehouse / factory layout available?</Typography>
              <RadioGroup row value={form.layoutAvailable} onChange={(e) => update('layoutAvailable', e.target.value)}>
                <FormControlLabel value="Yes" control={<Radio sx={{ color: '#bbb', '&.Mui-checked': { color: ORANGE } }} />} label="Yes" />
                <FormControlLabel value="No" control={<Radio sx={{ color: '#bbb', '&.Mui-checked': { color: ORANGE } }} />} label="No" />
              </RadioGroup>
            </FormSection>

            <FormSection number="05" title="Material Handling Equipment">
              <Typography sx={{ fontWeight: 700, color: DARK, mb: 1 }}>How will materials be loaded?</Typography>
              <FormGroup>
                <Grid container>
                  {handlingEquipment.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.handling.includes(item)}
                        onChange={() => update('handling', toggleInList(form.handling, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              <TextField
                fullWidth
                label="Other equipment"
                value={form.handlingOther}
                onChange={(e) => update('handlingOther', e.target.value)}
                sx={{ mt: 1, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
              <TextField
                fullWidth
                label="Maximum lifting height (if known)"
                value={form.maxLiftHeight}
                onChange={(e) => update('maxLiftHeight', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
            </FormSection>

            <FormSection number="06" title="Storage Preference" subtitle="What are your priorities? (Tick all that apply)">
              <FormGroup>
                <Grid container>
                  {priorities.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.priorities.includes(item)}
                        onChange={() => update('priorities', toggleInList(form.priorities, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </FormSection>

            <FormSection
              number="07"
              title="What is your main objective?"
              subtitle="You don't need to know rack types — tell us what you want to achieve and we'll recommend the right system."
            >
              <FormGroup>
                <Grid container>
                  {objectives.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.objectives.includes(item)}
                        onChange={() => update('objectives', toggleInList(form.objectives, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </FormSection>

            <FormSection number="08" title="Accessories Required (Optional)">
              <FormGroup>
                <Grid container>
                  {accessories.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.accessories.includes(item)}
                        onChange={() => update('accessories', toggleInList(form.accessories, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </FormSection>

            <FormSection number="09" title="Additional Information">
              <Typography sx={{ fontWeight: 700, color: DARK, mb: 1 }}>Please attach if available</Typography>
              <FormGroup>
                <Grid container>
                  {attachmentOptions.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <CheckboxField
                        label={item}
                        checked={form.attachments.includes(item)}
                        onChange={() => update('attachments', toggleInList(form.attachments, item))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Special requirements"
                value={form.specialRequirements}
                onChange={(e) => update('specialRequirements', e.target.value)}
                sx={{ mt: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
            </FormSection>

            <Paper elevation={0} sx={{ border: '1px solid #e6e6e6', borderRadius: 0, p: { xs: 2.5, md: 3 }, bgcolor: '#fff' }}>
              <Stack spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  endIcon={<SendIcon />}
                  sx={{
                    bgcolor: ORANGE,
                    borderRadius: 0,
                    fontWeight: 800,
                    py: 1.4,
                    '&:hover': { bgcolor: '#e66000' },
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Requirement'}
                </Button>
                {submitError ? (
                  <Typography sx={{ color: '#c62828', fontSize: '0.92rem', fontWeight: 600 }}>{submitError}</Typography>
                ) : null}
                {submitted ? (
                  <Typography sx={{ color: '#2e7d32', fontSize: '0.92rem', fontWeight: 600 }}>
                    Your requirement has been saved. WhatsApp opened so you can attach drawings and photos.
                  </Typography>
                ) : null}
                <Typography sx={{ color: MUTED, fontSize: '0.9rem', lineHeight: 1.6 }}>
                  Submitting opens WhatsApp with your answers pre-filled. You can attach drawings and photos in the chat.
                </Typography>
                <Divider />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneInTalkIcon sx={{ color: ORANGE }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>
                        Prefer to talk?
                      </Typography>
                      <Typography
                        component="a"
                        href={`tel:${PHONE_E164}`}
                        sx={{ color: DARK, fontWeight: 800, textDecoration: 'none', '&:hover': { color: ORANGE } }}
                      >
                        {PHONE_DISPLAY}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    component="a"
                    href={whatsappUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={<WhatsAppIcon />}
                    sx={{ borderRadius: 0, fontWeight: 800, borderColor: '#25D366', color: '#1a8f44', '&:hover': { borderColor: '#1ebe5d', bgcolor: 'rgba(37,211,102,0.06)' } }}
                  >
                    WhatsApp Direct
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
